"use strict";
'use client';

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.getSizes = exports.getMinimumFillValue = exports.ProgressCircle = void 0;
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _dataEncoreIds = require("../../dataEncoreIds.js");
var _index = require("../../styles/index.js");
var _StyledProgressCircleSvg = _interopRequireDefault(require("./StyledProgressCircleSvg.js"));
var _logging = require("../../logging.js");
var _styledComponents = _interopRequireDefault(require("../../styled-components.js"));
var _excluded = ["hasMinimumFill", "semanticColor", "size", "showTrack", "value", "variant", "aria-label", "aria-labelledby", "valuetext"];
var ProgressCircleContainer = _styledComponents["default"].div.withConfig({
  displayName: "ProgressCircle__ProgressCircleContainer",
  componentId: "sc-1j82oe0-0"
})(["display:inline-flex;"]);

/* eslint-enable local-rules/document-properties */

var getStrokeWidth = function getStrokeWidth(size) {
  switch (size) {
    case 'xsmall':
      return 1;
    case 'xlarge':
    case 'xxlarge':
      return 4;
    case 'small':
    case 'medium':
    case 'large':
    default:
      return 2;
  }
};
var getSizes = exports.getSizes = function getSizes(size) {
  var baseSize = (0, _index.getIconNumeric)(size);
  var strokeWidth = getStrokeWidth(size);
  var radius = baseSize / 2 - strokeWidth;
  return {
    viewBox: baseSize,
    cx: baseSize / 2,
    cy: baseSize / 2,
    r: radius,
    strokeWidth: strokeWidth,
    circumference: 2 * Math.PI * radius,
    iconSize: size === 'xsmall' ? 16 : baseSize
  };
};
var getMinimumFillValue = exports.getMinimumFillValue = function getMinimumFillValue(value) {
  if (value < 4) {
    return 4;
  } else if (value > 98) {
    return 98;
  }
  return value;
};
var getGeneratedText = function getGeneratedText(valuetext, valueProp, isDeterminate) {
  var generatedText;
  if (typeof valuetext === 'function') {
    generatedText = valuetext(valueProp);
  } else if (typeof valuetext === 'string') {
    generatedText = valuetext;
  } else if (!isDeterminate) {
    generatedText = 'Loading'; // ultimate fallback if one is not passed in.
  }
  if (isDeterminate && typeof valuetext === 'string' && !valuetext.includes(String(valueProp))) {
    (0, _logging.warn)('The provided aria-valuetext does not include the current value, which is non-compliant with WCAG for determinate progress bars.');
  }
  return generatedText;
};

/**
 * ProgressCircle tells users about the current status of a performed operation awaiting result, displayed using a spinner circle.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-progresscircle--docs) |
 * [Encore site](https://encore.spotify.net/web/components/progress-indicators#progress-circle) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/ProgressCircle) |
 * [Figma](https://www.figma.com/design/bnQXqGkLeiTlbk8JXGDcsX/%E2%9A%99%EF%B8%8F-%E2%80%8A%E2%80%8BEncore-Web-Components?m=auto&node-id=32403-12342&t=SD3uNBTopxXkK8PY-1)
 */
var ProgressCircle = exports.ProgressCircle = /*#__PURE__*/_react["default"].forwardRef(function ProgressCircle(_ref, ref) {
  var _ref$hasMinimumFill = _ref.hasMinimumFill,
    hasMinimumFill = _ref$hasMinimumFill === void 0 ? true : _ref$hasMinimumFill,
    _ref$semanticColor = _ref.semanticColor,
    semanticColor = _ref$semanticColor === void 0 ? 'textBase' : _ref$semanticColor,
    _ref$size = _ref.size,
    sizeProp = _ref$size === void 0 ? 'medium' : _ref$size,
    _ref$showTrack = _ref.showTrack,
    showTrack = _ref$showTrack === void 0 ? true : _ref$showTrack,
    _ref$value = _ref.value,
    valueProp = _ref$value === void 0 ? 0 : _ref$value,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? 'indeterminate' : _ref$variant,
    ariaLabel = _ref['aria-label'],
    ariaLabelledby = _ref['aria-labelledby'],
    valuetext = _ref.valuetext,
    props = (0, _objectWithoutProperties2["default"])(_ref, _excluded);
  var size = getSizes(sizeProp);
  var value;
  var isDeterminate = variant === 'determinate';
  var generatedText = getGeneratedText(valuetext, valueProp, isDeterminate);
  if (isDeterminate) {
    value = size.circumference * (hasMinimumFill ? getMinimumFillValue(valueProp) : valueProp) / 100;
  } else {
    value = size.circumference;
  }
  if (variant === 'indeterminate' && valueProp) {
    (0, _logging.warn)('Warning: No value should be passed when the variant is indeterminate. Please either remove the value prop or change the variant to determinate.');
  }
  if (!ariaLabel && !ariaLabelledby) {
    (0, _logging.warn)('ProgressCircle must have an aria-label or aria-labelledby attribute defined in order to be accessible.');
  }
  return /*#__PURE__*/_react["default"].createElement(ProgressCircleContainer, Object.assign({
    role: "progressbar",
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby,
    "aria-valuetext": generatedText,
    "aria-valuenow": isDeterminate ? valueProp : '',
    "aria-valuemin": isDeterminate ? 0 : undefined,
    "aria-valuemax": isDeterminate ? 100 : undefined,
    "data-encore-id": _dataEncoreIds.ENCORE_DATA_IDS.ProgressCircle,
    ref: ref
  }, props), /*#__PURE__*/_react["default"].createElement(_StyledProgressCircleSvg["default"], {
    "data-testid": "progress-circle",
    viewBox: "0 0 ".concat(size.viewBox, " ").concat(size.viewBox),
    $variant: variant,
    $size: sizeProp,
    $circumference: size.circumference
    // $tokenType="decorative"
  }, /*#__PURE__*/_react["default"].createElement("g", {
    fill: "none"
  }, showTrack && /*#__PURE__*/_react["default"].createElement("circle", {
    cx: size.cx,
    cy: size.cy,
    r: size.r,
    strokeWidth: size.strokeWidth,
    stroke: (0, _index.cssColorValue)('decorativeSubdued'),
    "data-testid": "track"
  }), /*#__PURE__*/_react["default"].createElement("circle", {
    cx: size.cx,
    cy: size.cy,
    r: size.r,
    strokeWidth: size.strokeWidth,
    strokeDasharray: "".concat(value, ", ").concat(size.circumference),
    stroke: (0, _index.cssColorValue)(semanticColor),
    "data-testid": "filled-track"
  }))));
});